import { useState, useEffect } from "react";
import { Search } from "lucide-react";
import styles from "../../../styles/__labServicesAndPricing.module.scss";
import { useTranslation } from "react-i18next";
import { IoAddCircleOutline } from "react-icons/io5";
import { FaRegEdit } from "react-icons/fa";
import { FiTrash2 } from "react-icons/fi";
import PhysioDialogAddEditTest from "../../../components/physio-dialog-AddEditTest/PhysioDialogAddEditTest";
import PhysioDashboardLayout from "../../../components/physio-dashboard-layout/PhysioDashboardLayout";
import { toast } from '../../../components/toast';
import axios from "axios";

const createAxiosInstance = () => {
  const token = sessionStorage.getItem("usr_1a2b3c");
  return axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
};

const PhysioServicesAndPricing = ({ onLogout, onNavigateToDashboard }) => {
  const { t } = useTranslation();

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 10;

  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState("add");
  const [selectedTest, setSelectedTest] = useState(null);

  // Fetch services
  const fetchServices = async (page = 1, search = searchTerm, status = statusFilter) => {
    setLoading(true);
    try {
      const axiosInstance = createAxiosInstance();
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("limit", itemsPerPage.toString());
      if (search) params.append("search", search);
      if (status) params.append("status", status);

      const response = await axiosInstance.get(`/api/v1/physiotherapist/services?${params.toString()}`);

      if (response?.data?.status && response.data.data) {
        const data = response.data.data;
        setServices(data.services || []);
        setTotalPages(data.pagination?.totalPages || 1);
        setTotalCount(data.pagination?.total || 0);
        setCurrentPage(page);
      }
    } catch (error) {
      console.error("Failed to fetch services:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices(1);
  }, []);

  const handleSearch = () => {
    fetchServices(1, searchTerm, statusFilter);
  };

  const resetFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setCurrentPage(1);
    fetchServices(1, "", "");
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    fetchServices(page);
  };

  const handleAddNew = () => {
    setDialogType("add");
    setSelectedTest(null);
    setOpenDialog(true);
  };

  const handleEdit = (service) => {
    setDialogType("edit");
    setSelectedTest({
      id: service.id,
      serviceCode: service.service_code || service.serviceCode || "",
      testName: service.test_name || service.serviceName,
      testNameArabic: service.service_name_arabic || service.serviceNameArabic || "",
      serviceCategoryId: service.category_id || service.service_category_id || service.serviceCategoryId,
      price: service.price,
      tatHours: service.tat_hours || service.tatHours,
      location: service.location,
      status: service.status,
    });
    setOpenDialog(true);
  };

  const handleDelete = async (service) => {
    if (!window.confirm(`Are you sure you want to delete "${service.test_name || service.serviceName}"?`)) return;
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.delete(`/api/v1/physiotherapist/services/${service.id}`);
      if (response?.data?.status) {
        toast.success("Service deleted successfully");
        fetchServices(currentPage);
      } else {
        toast.error(response?.data?.message || "Failed to delete service");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete service");
    }
  };

  const handleNavigate = (page) => {
    if (page === "dashboard") onNavigateToDashboard();
  };

  return (
    <PhysioDashboardLayout
      currentPage="physio_services"
      onNavigate={handleNavigate}
      onLogout={onLogout}
    >
      <PhysioDialogAddEditTest
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        type={dialogType}
        selectedTest={selectedTest}
        onSuccess={() => fetchServices(currentPage)}
      />

      <>
        <div className={styles["apt-page-header"]}>
          <div>
            <h1>Services and Pricing</h1>
            <p className={styles["apt-page-subtitle"]}>Manage available tests, prices, and turnaround times.</p>
          </div>
          <button className={styles["apt-register-btn"]} onClick={handleAddNew}>
            <IoAddCircleOutline /> Add New Service
          </button>
        </div>

        <div className={styles["apt-appointments-section"]}>
          {/* Filters */}
          <div className={styles["apt-filters"]}>
            <div className={styles["apt-filter-group"]}>
              <div className={styles["apt-search-wrapper"]}>
                <Search className={styles["apt-search-icon"]} />
                <input
                  type="text"
                  placeholder="Service Name"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  className={styles["apt-search-input"]}
                />
              </div>
              <select
                className={styles["apt-status-filter"]}
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  fetchServices(1, searchTerm, e.target.value);
                }}
              >
                <option value="">Status</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
              <button className={styles["apt-reset-btn"]} onClick={resetFilters}>
                Clear Filters
              </button>
            </div>
          </div>

          {/* Table */}
          <div className={styles["apt-appointments-table"]}>
            <table>
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>Service Code</th>
                  <th style={{ textAlign: "left" }}>Service Name</th>
                  <th style={{ textAlign: "center" }}>Service Name (Arabic)</th>
                  <th style={{ textAlign: "center" }}>Category</th>
                  <th style={{ textAlign: "center" }}>Price (IQD)</th>
                  <th style={{ textAlign: "center" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="6" style={{ textAlign: "center", padding: "24px" }}>Loading...</td></tr>
                ) : services.length === 0 ? (
                  <tr><td colSpan="6" style={{ textAlign: "center", padding: "24px", color: "#89899b" }}>No records found</td></tr>
                ) : (
                  services.map((service, idx) => (
                    <tr key={service.id || idx}>
                      <td style={{ textAlign: "left" }}>{service.service_code || service.serviceCode || "—"}</td>
                      <td style={{ textAlign: "left" }}>{service.test_name || service.serviceName || "—"}</td>
                      <td style={{ direction: "rtl", textAlign: "center" }}>{service.service_name_arabic || service.serviceNameArabic || "—"}</td>
                      <td style={{ textAlign: "center" }}>{service.category_name || "—"}</td>
                      <td style={{ textAlign: "center" }}>{Math.round(Number(service.price || 0)).toLocaleString()}</td>
                      <td style={{ textAlign: "center" }}>
                        <div style={{ display: "flex", gap: "8px" }}>
                          <button
                            className={styles["apt-action-btn"]}
                            title="Edit"
                            onClick={() => handleEdit(service)}
                          >
                            <FaRegEdit size={16} color="#155DFC" />
                          </button>
                          <button
                            className={styles["apt-action-btn"]}
                            title="Delete"
                            onClick={() => handleDelete(service)}
                          >
                            <FiTrash2 size={16} color="#e5252a" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className={styles["apt-pagination"]}>
            <div className={styles["apt-pagination-info"]}>
              Displaying Results {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, totalCount)} Out of {totalCount}
            </div>
            <div className={styles["apt-pagination-controls"]}>
              <button
                className={`${styles["apt-pagination-btn"]} ${currentPage === 1 ? styles["disabled"] : ""}`}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous Page
              </button>
              <div className={styles["apt-page-numbers"]}>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i + 1}
                    className={`${styles["apt-page-btn"]} ${currentPage === i + 1 ? styles["active"] : ""}`}
                    onClick={() => handlePageChange(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
              <button
                className={`${styles["apt-pagination-btn"]} ${styles["apt-next-btn"]} ${currentPage === totalPages ? styles["disabled"] : ""}`}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next Page
              </button>
            </div>
          </div>
        </div>
      </>
    </PhysioDashboardLayout>
  );
};

export default PhysioServicesAndPricing;
